import { ArrowUpRight, Users, LineChart, Handshake, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Stat = ({ n, label }) => (
  <div className="border-l border-neutral-800 pl-6">
    <div className="font-display text-4xl md:text-5xl font-black text-white tabular">{n}</div>
    <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
  </div>
);

const Feature = ({ Icon, title, desc, tid }) => (
  <div data-testid={tid} className="hairline p-8 bg-[#101010] hover:bg-[#141414] transition-colors group">
    <div className="w-10 h-10 flex items-center justify-center bg-[#39FF14] text-black mb-6">
      <Icon size={20} strokeWidth={2.5} />
    </div>
    <h3 className="font-display text-xl font-bold mb-2">{title}</h3>
    <p className="text-sm text-neutral-400 leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain relative">
      {/* Header */}
      <header className="border-b border-neutral-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_realtor-sync-hub/artifacts/qtyg2nbs_3badc621-45b2-4a92-9947-76cdba8c51d3.png"
              alt="Kreative KPI — Blue to Green to Gold"
              className="w-12 h-12 object-contain"
            />
            <span className="font-display font-black text-lg tracking-tight">KREATIVE KPI</span>
          </div>
          <Button
            data-testid="landing-login-btn"
            onClick={handleLogin}
            className="bg-[#39FF14] text-black hover:bg-white rounded-none font-semibold uppercase tracking-wider text-xs h-10 px-5"
          >
            Sign in with Google <ArrowUpRight size={14} className="ml-1.5" />
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-32 pb-24">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 hairline px-3 py-1.5 mb-8 text-[10px] uppercase tracking-widest text-neutral-400">
              <span className="w-1.5 h-1.5 bg-[#39FF14] pulse-dot" /> Built for real estate wholesalers
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-black leading-[0.92] tracking-tight">
              Track KPIs.<br />
              <span className="text-neutral-500">Lead teams.</span><br />
              <span className="text-[#39FF14]">Close deals</span> together.
            </h1>
            <p className="mt-8 text-neutral-400 text-base md:text-lg max-w-xl leading-relaxed">
              The ops platform where wholesalers hit daily numbers, team leaders monitor performance in
              real time, and accountability partners keep each other honest — until it becomes revenue.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                data-testid="landing-hero-cta"
                onClick={handleLogin}
                className="bg-[#39FF14] text-black hover:bg-white rounded-none font-semibold uppercase tracking-wider text-xs h-12 px-6"
              >
                Start free — Google sign-in <ArrowUpRight size={16} className="ml-2" />
              </Button>
              <a
                href="#features"
                data-testid="landing-features-link"
                className="hairline h-12 px-6 inline-flex items-center text-xs uppercase tracking-widest text-neutral-300 hover:text-[#39FF14] transition-colors"
              >
                See how it works
              </a>
            </div>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <div className="hairline bg-[#101010] p-6 w-full max-w-xs">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-4">Today · Live</div>
              <div className="space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-neutral-400">Calls</span>
                  <span className="font-display text-2xl font-bold tabular">142</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-neutral-400">Leads</span>
                  <span className="font-display text-2xl font-bold tabular">17</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-neutral-400">Contracts</span>
                  <span className="font-display text-2xl font-bold text-[#39FF14] tabular">3</span>
                </div>
                <div className="h-px bg-neutral-800 my-3" />
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-neutral-400">Revenue</span>
                  <span className="font-display text-2xl font-bold text-[#39FF14] tabular">$48K</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-neutral-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          <Stat n="8" label="KPIs tracked daily" />
          <Stat n="5" label="Wholesaling roles" />
          <Stat n="1:1" label="Partner check-ins" />
          <Stat n="∞" label="Google Docs exports" />
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-3">// The system</div>
            <h2 className="font-display text-4xl md:text-5xl font-black max-w-2xl leading-tight">
              Three layers that turn <span className="text-neutral-500">activity</span> into <span className="text-[#39FF14]">assignments</span>.
            </h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Feature
            tid="feature-kpi"
            Icon={LineChart}
            title="Daily KPI logging"
            desc="Log calls, contacts, leads, appointments, offers, contracts, deals closed and revenue in seconds. Export weekly to Google Docs."
          />
          <Feature
            tid="feature-team"
            Icon={Users}
            title="Team leader dashboards"
            desc="Live leaderboards, per-member drill-down, and week-over-week trends so you know who to coach — and when."
          />
          <Feature
            tid="feature-partners"
            Icon={Handshake}
            title="Accountability partners"
            desc="Match with wholesalers in the same or complementary roles. Shared goals, side-by-side dashboards, chat, daily check-ins."
          />
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-neutral-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h3 className="font-display text-4xl md:text-6xl font-black tracking-tight max-w-2xl leading-none">
            The KPI is not the goal.<br />
            <span className="text-[#39FF14]">The deal is.</span>
          </h3>
          <Button
            data-testid="landing-footer-cta"
            onClick={handleLogin}
            className="bg-[#39FF14] text-black hover:bg-white rounded-none font-semibold uppercase tracking-wider text-xs h-12 px-6"
          >
            Get started
          </Button>
        </div>
        <div className="border-t border-neutral-900 py-6 text-center text-[10px] uppercase tracking-widest text-neutral-600">
          © Kreative KPI
        </div>
      </section>
    </div>
  );
}
